import { createInertiaApp as baseCreateInertiaApp } from "@inertiajs/react";
import { renderApp } from "@inertiaui/modal-react";
import {
  Component,
  createElement,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { configure } from "@/_generated/routes/routes";
import PersistentLayout from "@/layouts/persistent-layout.tsx";
import type { BizminerInertiaSharedProps } from "@/types/inertia.ts";

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode | any;
  layout?: (page: ReactNode) => ReactNode;
};

interface CreateMinerAppOptions {
  namespace: string | string[];
}

function ErrorFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
        <p className="text-muted-foreground mb-4">
          An unexpected error occurred. Please reload the page to continue.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("React Error Boundary caught:", error, info.componentStack);
  }

  render() {
    return this.state.hasError ? <ErrorFallback /> : this.props.children;
  }
}

export function createPageResolver(namespaces: string[]) {
  const namespacePattern = namespaces.join("|");

  return (name: string) => {
    // Vite requires static glob patterns, so we hardcode the valid namespaces
    const pages = import.meta.glob<ResolvedComponent>(
      [
        "../salesminer/**/*.tsx",
        "../bizminer/**/*.tsx",
        "!../**/*.test.tsx",
        "!../**/*.spec.tsx",
      ],
      { eager: true },
    );

    // Match namespace prefix: "Salesminer/Admin/Sessions/New" or "Bizminer/App/Dashboard/Index"
    const namespaceMatch = name.match(
      new RegExp(`^(${namespacePattern})\\/(.+)$`),
    );

    let pagePath: string;
    if (namespaceMatch) {
      // Namespaced: "Salesminer/Admin/Sessions/New" -> "../salesminer/Admin/Sessions/New.tsx"
      const matchedNamespace = namespaceMatch[1].toLowerCase();
      const componentPath = namespaceMatch[2];
      pagePath = `../${matchedNamespace}/${componentPath}.tsx`;
    } else {
      const expectedFormats = namespaces
        .map((ns) => `'${ns}/YourComponent'`)
        .join(" or ");
      throw new Error(
        `Inertia page component '${name}' is missing required namespace prefix.\n` +
          `Expected format: ${expectedFormats}\n` +
          `Got: '${name}'`,
      );
    }

    const page = pages[pagePath];

    if (!page) {
      console.error(
        `Missing Inertia page component: '${name}' (resolved to: ${pagePath})`,
      );
      throw new Error(`Missing Inertia page component: '${name}'`);
    }

    page.default.layout ??= (page: any) =>
      createElement(PersistentLayout, null, page);

    return page;
  };
}

export function createMinerApp({ namespace }: CreateMinerAppOptions) {
  const namespaces = Array.isArray(namespace) ? namespace : [namespace];

  return baseCreateInertiaApp({
    resolve: createPageResolver(namespaces),

    setup({ el, App, props }) {
      // Configure js-routes BEFORE React renders
      // This ensures default_url_options are set when route helpers are called during render
      const initialProps = props.initialPage
        .props as unknown as BizminerInertiaSharedProps;
      configure({
        default_url_options: initialProps.default_url_options,
      });

      if (el) {
        const appWithErrorBoundary = (
          <ErrorBoundary>{renderApp(App, props)}</ErrorBoundary>
        );

        // Check if we have server-rendered content to hydrate
        if (el.innerHTML) {
          hydrateRoot(el, appWithErrorBoundary);
        } else {
          createRoot(el).render(appWithErrorBoundary);
        }
      } else {
        const namespaceList = namespaces
          .map((ns) => ns.toLowerCase())
          .join(", ");
        console.error(
          "Missing root element.\\n\\n" +
            "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\\n" +
            `Consider moving the vite_typescript_tag to an Inertia-specific layout (namespaces: ${namespaceList}).`,
        );
      }
    },
  });
}
